import type { BiomeId } from './biomes';

/**
 * Cosmetics (docs/GDD.md §10). Rare finds on the map award most of them; each zone's own one is
 * earned only by every star in that zone (owner, 2026-10-04), never found or sold.
 */
export type CosmeticSlot = 'stripes' | 'paint';

export interface CosmeticDef {
  id: string;
  name: string;
  slot: CosmeticSlot;
  /** A zone's All-Stars reward: earned by every star on its lawns, never found or sold. */
  zone?: BiomeId;
  /** The thank-you for unlocking the full game: never found on a lawn. */
  gift?: true;
  /** Earned by tagging a squirrel or rabbit (a bump: it hops off unharmed), never found or sold. */
  critter?: true;
  /** Earned by sharing the game five times (Settings > TELL A FRIEND), never found or sold. */
  share?: true;
  /** Earned by ending a week first on the featured board (online play), never found or sold. */
  crown?: true;
  /** Earned by every star in the whole campaign (owner, 2026-10-29), never found or sold. */
  grand?: true;
  /**
   * Earned by finding every secret yard (owner, 2026-10-30), never found or sold. Yards are never
   * hinted at, so until it is won the Garage calls it only a secret.
   */
  yards?: true;
  /** Earned by mowing every campaign lawn, stars or not (owner, 2026-11-04), never found or sold. */
  cleared?: true;
}

export const COSMETICS: readonly CosmeticDef[] = [
  { id: 'stripes-checker', name: 'Checkerboard Stripes', slot: 'stripes' },
  { id: 'stripes-diagonal', name: 'Diagonal Stripes', slot: 'stripes' },
  { id: 'stripes-wide', name: 'Wide Stripes', slot: 'stripes' },
  { id: 'stripes-plaid', name: 'Plaid Stripes', slot: 'stripes' },
  { id: 'paint-mint', name: 'Mint Paint', slot: 'paint' },
  { id: 'paint-sunset', name: 'Sunset Paint', slot: 'paint' },
  { id: 'paint-camo', name: 'Camo Paint', slot: 'paint' },
  { id: 'paint-gold', name: 'Gold Paint', slot: 'paint' },
  // All-Stars rewards, one per zone.
  { id: 'stripes-picket', name: 'Picket Stripes', slot: 'stripes', zone: 'backyard' },
  { id: 'paint-fern', name: 'Fern Paint', slot: 'paint', zone: 'park' },
  { id: 'paint-barnred', name: 'Barn Red Paint', slot: 'paint', zone: 'farm' },
  { id: 'stripes-argyle', name: 'Argyle Stripes', slot: 'stripes', zone: 'golf' },
  { id: 'paint-moonlight', name: 'Moonlight Paint', slot: 'paint', zone: 'cemetery' },
  { id: 'stripes-royal', name: 'Royal Plaid', slot: 'stripes', zone: 'estate' },
  { id: 'paint-champion', name: 'Champion Paint', slot: 'paint', zone: 'stadium' },
  { id: 'stripes-meadow', name: 'Meadow Waves', slot: 'stripes', zone: 'meadow' },
  // The full game's thank-you (docs/MONETIZATION.md).
  { id: 'paint-thanks', name: 'Thank-You Paint', slot: 'paint', gift: true },
  // The first squirrel or rabbit tagged (owner, 2026-10-21: catching one is a fun challenge).
  { id: 'stripes-paws', name: 'Paw Print Stripes', slot: 'stripes', critter: true },
  // Five shares of the game (owner, 2026-10-21: a vanity reward for telling friends).
  { id: 'paint-megaphone', name: 'Megaphone Paint', slot: 'paint', share: true },
  { id: 'stripes-chat', name: 'Word of Mouth Stripes', slot: 'stripes', share: true },
  // First on the week's featured board (owner, 2026-10-25: a crown on the boards, and this paint).
  { id: 'paint-crown', name: 'Crown Paint', slot: 'paint', crown: true },
  // Every star in the whole campaign (owner, 2026-10-29): an animated rainbow tie-dye, twinkling.
  { id: 'paint-stellar', name: 'Stellar Tie-Dye', slot: 'paint', grand: true },
  // Every secret yard found (owner, 2026-10-30): hedge and ivy, flowers opening, a gold glint.
  { id: 'paint-secret', name: 'Secret Garden', slot: 'paint', yards: true },
  // Every campaign lawn mowed, stars or not (owner, 2026-11-04): a racing checker, flag-red edges.
  { id: 'paint-finish', name: 'Finish Line Paint', slot: 'paint', cleared: true },
];

/** The cosmetic for every star in the whole campaign. */
export const GRAND_COSMETIC = 'paint-stellar';

/** The coin bonus that comes with it. */
export const GRAND_COINS = 1000;

/** The cosmetic for finding every secret yard, and its coin bonus. */
export const YARDS_COSMETIC = 'paint-secret';
export const YARDS_COINS = 500;

/** The cosmetic for mowing every campaign lawn (stars not needed), and its coin bonus. */
export const CLEARED_COSMETIC = 'paint-finish';
export const CLEARED_COINS = 500;

/** The cosmetic for ending a featured week first (docs/ONLINE.md, "Boards"). */
export const CROWN_COSMETIC = 'paint-crown';

/** The cosmetic for the first squirrel or rabbit tagged. */
export const CRITTER_TAG_COSMETIC = 'stripes-paws';

/** The cosmetics for sharing the game five times. */
export const SHARE_COSMETICS = COSMETICS.filter((c) => c.share).map((c) => c.id);

/**
 * Cosmetics a rare find can award: not the zones' All-Stars rewards, the thank-you, the critter
 * tag's, the sharing prize or the crown's. (The pool picks each lawn's find by hash: adding to it would move
 * existing finds.)
 */
export const FINDABLE_COSMETICS = COSMETICS.filter(
  (c) =>
    !c.zone && !c.gift && !c.critter && !c.share && !c.crown && !c.grand && !c.yards && !c.cleared,
);

/** A zone's All-Stars cosmetic. */
export function zoneCosmetic(zone: BiomeId): CosmeticDef {
  const c = COSMETICS.find((d) => d.zone === zone);
  if (!c) throw new Error(`No All-Stars cosmetic for ${zone}`);
  return c;
}
